/**
 * Композиция провайдеров и глобальных подписок.
 *
 * Здесь же «приземляются» коды ошибок, которые обрабатывает приложение
 * целиком, а не экран (таблица CLIENT_WEB.md §2.3).
 */
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'

import { onGlobalApiError } from '@shared/api/client'
import { ToastProvider } from '@shared/ui'
import { onTabMessage } from '@shared/ws/leader'
import { syncEngine } from '@shared/sync/instance'

import { useAppStore } from '../model/app-store'
import { watchSystemTheme } from '../model/theme'
import { startActiveChatMirror } from '../boot/active-chat'
import { CHAT_SEARCH_ATTR, startHotkeys } from '../model/hotkeys'
import { router } from '../routes/router'
import { startServiceWorkerBridge } from '../boot/sw-bridge'
import { handleSessionLost } from '../boot/session-lifecycle'

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Экран читает данные из реплики; кэш запросов — только слой чтения
        // поверх неё (CC §4.1 п. 2), поэтому агрессивный refetch не нужен.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          // Повторяем только сетевые отказы: коды контракта решает обработчик.
          const isNetwork = error instanceof TypeError || error?.constructor?.name === 'NetworkError'
          return isNetwork && failureCount < 2
        },
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  })
}

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient)

  useGlobalErrorHandling()
  useNetworkState()
  useTabMessages()
  useForegroundReconnect()

  useEffect(() => watchSystemTheme(), [])
  // §8.4: маршруты из уведомлений и обновление клиента (SYS-04).
  useEffect(() => startServiceWorkerBridge(), [])
  // §8.5 п. 1–2: отметка «диалог открыт» живёт вместе с видимостью вкладки.
  useEffect(() => startActiveChatMirror(), [])
  // §4.6: горячие клавиши desktop (D-02).
  useDesktopHotkeys()

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>{children}</ToastProvider>
    </QueryClientProvider>
  )
}

/* ===================================================================== */
/* Глобальные коды ошибок (§2.3)                                          */
/* ===================================================================== */

function useGlobalErrorHandling(): void {
  useEffect(
    () =>
      onGlobalApiError((error) => {
        const store = useAppStore.getState()

        switch (error.code) {
          case 'session_expired':
          case 'unauthenticated':
            /*
             * 401 на старте — обычный путь первого запуска (сессии просто нет),
             * а не потеря сессии. SYS-07 показывается ТОЛЬКО когда сессия
             * была: иначе пользователь увидел бы «нужно войти снова», ни разу
             * не войдя.
             */
            if (store.phase === 'ready' || store.phase === 'onboarding') {
              void handleSessionLost()
            } else {
              store.setPhase('unauthenticated')
            }
            break

          case 'account_restricted':
            store.setBlocking({ kind: 'account_restricted' })
            break

          case 'account_suspended':
            store.setBlocking({ kind: 'account_suspended' })
            break

          case 'account_pending_deletion':
            store.setBlocking({ kind: 'account_pending_deletion' })
            break

          case 'consent_required':
            // Фоновые запросы и reconnect приостанавливаются до POST /me/consents.
            syncEngine()?.suspendForConsent?.()
            store.setBlocking({ kind: 'consent_required' })
            break

          case 'client_upgrade_required':
            store.setBlocking({ kind: 'client_upgrade_required' })
            void navigator.serviceWorker?.getRegistration().then((r) => r?.update())
            break

          case 'csrf_rejected':
            store.setBlocking({ kind: 'diagnostic', code: error.code, requestId: error.requestId })
            break

          case 'onboarding_incomplete':
            store.setPhase('onboarding')
            break

          default:
            break
        }
      }),
    [],
  )
}

/* ===================================================================== */
/* Сетевое состояние (CC §3.2)                                            */
/* ===================================================================== */

function useNetworkState(): void {
  useEffect(() => {
    const goOffline = (): void => useAppStore.getState().setOnline(false)
    const goOnline = (): void => {
      // `navigator.onLine` — лишь подсказка: подтверждением служит успешный
      // обмен с сервером (CC §3.2), и его подтверждает движок.
      syncEngine()?.confirmOnline()
    }

    globalThis.addEventListener('offline', goOffline)
    globalThis.addEventListener('online', goOnline)

    if (!navigator.onLine) goOffline()

    return () => {
      globalThis.removeEventListener('offline', goOffline)
      globalThis.removeEventListener('online', goOnline)
    }
  }, [])
}

/* ===================================================================== */
/* Сообщения между вкладками (§2.4 п. 4–5)                                */
/* ===================================================================== */

function useTabMessages(): void {
  useEffect(
    () =>
      onTabMessage((message) => {
        switch (message.type) {
          case 'logout':
          case 'owner_changed':
            // База удаляется намеренно — показываем вход, а не «нужна перезагрузка».
            useAppStore.getState().reset()
            break

          case 'force_reload':
            location.reload()
            break

          default:
            break
        }
      }),
    [],
  )
}

/* ===================================================================== */
/* Горячие клавиши desktop (§4.6, D-02)                                   */
/* ===================================================================== */

function useDesktopHotkeys(): void {
  useEffect(
    () =>
      startHotkeys({
        openShortcuts: () => useAppStore.getState().setShortcutsOpen(true),

        /*
         * `/` и `Ctrl/Cmd + K` адресуют поиск по списку диалогов (C-02).
         * Если список не открыт, сначала выполняется переход — действие
         * остаётся тем же, что и по нажатию кнопки поиска мышью.
         */
        focusChatSearch: () => {
          const focus = (): void => {
            const input = document.querySelector<HTMLElement>(`[${CHAT_SEARCH_ATTR}]`)
            if (input) {
              input.focus()
              return
            }
            // Поиск свёрнут — просим экран его открыть.
            document.querySelector<HTMLElement>('[data-hotkey-chat-search-toggle]')?.click()
            requestAnimationFrame(() => document.querySelector<HTMLElement>(`[${CHAT_SEARCH_ATTR}]`)?.focus())
          }

          if (!router.state.location.pathname.startsWith('/chats')) {
            void router.navigate({ to: '/chats' }).then(() => requestAnimationFrame(focus))
            return
          }

          focus()
        },
      }),
    [],
  )
}

/* ===================================================================== */
/* Возврат вкладки в foreground: PWA-05 (CC §4.5 п. 7)                    */
/* ===================================================================== */

function useForegroundReconnect(): void {
  useEffect(() => {
    const handler = (): void => {
      if (document.visibilityState !== 'visible') return
      syncEngine()?.reconnectNow()
    }

    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])
}
